import { useCallback, useMemo } from "react";
import { useRegions } from "../dictionaries/useRegions";
import { usePackages } from "./usePackages";

interface PackageIdItem {
  id: string;
  userFriendlyId: string;
}

export interface IdValidationResult {
  valid: boolean;
  error?: "required" | "invalid" | "duplicate" | "prefix";
}

function numberToLetters(value: number): string {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`Value must be a positive integer, got: ${value}`);
  }

  let result = "";

  while (value > 0) {
    value--;
    result = String.fromCharCode(97 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }

  return result;
}

function lettersToNumber(value: string): number {
  if (!/^[a-z]+$/i.test(value)) {
    throw new Error(`Invalid letters: "${value}"`);
  }

  let result = 0;

  for (const character of value.toLowerCase()) {
    result = result * 26 + character.charCodeAt(0) - 96;
  }

  return result;
}

function isValidPackageUserFriendlyId(value: string): boolean {
  const parts = value.split(".");

  if (parts.some((part) => !part) || parts.length > 3) {
    return false;
  }

  const isNumber = (part: string) => /^[1-9]\d*$/.test(part);
  const isLetters = (part: string) => /^[a-z]+$/i.test(part);

  if (parts.length === 1) {
    return isNumber(parts[0]) || isLetters(parts[0]);
  }

  if (parts.length === 2) {
    return (isNumber(parts[0]) && isNumber(parts[1])) || (isNumber(parts[0]) && isLetters(parts[1]));
  }

  return isNumber(parts[0]) && isNumber(parts[1]) && isLetters(parts[2]);
}

function getPackageUserFriendlyIdPrefix(
  regionUserFriendlyId?: number | null,
  parentUserFriendlyId?: string | null,
): string {
  if (parentUserFriendlyId) {
    return `${parentUserFriendlyId}.`;
  }

  if (regionUserFriendlyId) {
    return `${regionUserFriendlyId}.`;
  }

  return "";
}

function getNextPackageUserFriendlyId(
  packages: PackageIdItem[],
  regionUserFriendlyId?: number | null,
  parentUserFriendlyId?: string | null,
): string {
  const parent = parentUserFriendlyId?.trim().toLowerCase();
  const region = regionUserFriendlyId?.toString() ?? null;
  const used = new Set<number>();

  for (const item of packages) {
    const id = item.userFriendlyId.trim().toLowerCase();
    const parts = id.split(".");

    if (parent) {
      // Podpakiety numerujemy w obrębie rodzica.
      if (parts.length !== parent.split(".").length + 1) {
        continue;
      }

      const lastPart = parts.at(-1)!;
      const itemParent = parts.slice(0, -1).join(".");

      if (itemParent === parent && /^[a-z]+$/.test(lastPart)) {
        used.add(lettersToNumber(lastPart));
      }

      continue;
    }

    // Pakiety główne numerujemy lokalnie w obrębie regionu.
    const itemRegion = parts.length >= 2 ? parts[0] : null;

    if (itemRegion !== region) {
      continue;
    }

    // 2, 1.2 oraz 1.2.a zajmują ten sam numer pakietu.
    const packageIdPart = parts.length >= 2 ? parts[1] : parts[0];

    if (/^[1-9]\d*$/.test(packageIdPart)) {
      used.add(Number(packageIdPart));
    }
  }

  let nextId = 1;

  while (used.has(nextId)) {
    nextId++;
  }

  if (parent) {
    return `${parent}.${numberToLetters(nextId)}`;
  }

  if (region !== null) {
    return `${region}.${nextId}`;
  }

  return String(nextId);
}

/**
 * Wewnętrzny hook współdzielący dane pomiędzy publicznymi hookami.
 */
function usePackageUserFriendlyIdData(regionId?: string | null, parentPackageId?: string | null) {
  const { packages, isLoading: packagesLoading } = usePackages();
  const { regions, isLoading: regionsLoading } = useRegions();

  const packageIds = useMemo<PackageIdItem[]>(
    () =>
      packages
        .filter((pkg) => pkg.id.userFriendlyId)
        .map((pkg) => ({
          id: pkg.id.id,
          userFriendlyId: pkg.id.userFriendlyId,
        })) as PackageIdItem[],
    [packages],
  );

  const parentUserFriendlyId = useMemo(
    () => packages.find((pkg) => pkg.id.id === parentPackageId)?.id.userFriendlyId ?? null,
    [packages, parentPackageId],
  );

  const regionUserFriendlyId = useMemo(
    () => regions.find((region) => region.id.id === regionId)?.id.userFriendlyId,
    [regions, regionId],
  );

  return {
    packageIds,
    parentUserFriendlyId,
    regionUserFriendlyId,
    isLoading: packagesLoading || regionsLoading,
  };
}

/**
 * Generuje domyślne ID dla nowego pakietu lub kopii.
 */
export function useNextPackageUserFriendlyId(regionId?: string | null, parentPackageId?: string | null) {
  const { packageIds, parentUserFriendlyId, regionUserFriendlyId, isLoading } = usePackageUserFriendlyIdData(
    regionId,
    parentPackageId,
  );

  const nextUserFriendlyId = useMemo(
    () => getNextPackageUserFriendlyId(packageIds, regionUserFriendlyId, parentUserFriendlyId),
    [packageIds, parentUserFriendlyId, regionUserFriendlyId],
  );

  return { nextUserFriendlyId, isLoading };
}

/**
 * Waliduje ID względem aktualnego rodzica, regionu i istniejących pakietów.
 */
export function usePackageUserFriendlyId(
  parentPackageId?: string | null,
  regionId?: string | null,
  currentPackageId?: string | null,
) {
  const {
    packageIds,
    parentUserFriendlyId,
    regionUserFriendlyId,
    isLoading: isDataLoading,
  } = usePackageUserFriendlyIdData(regionId, parentPackageId);
  const { nextUserFriendlyId, isLoading: isNextIdLoading } = useNextPackageUserFriendlyId(regionId, parentPackageId);

  const prefix = useMemo(
    () => getPackageUserFriendlyIdPrefix(regionUserFriendlyId, parentUserFriendlyId),
    [parentUserFriendlyId, regionUserFriendlyId],
  );

  const validate = useCallback(
    (value: string): IdValidationResult => {
      const normalized = value.trim().toLowerCase();

      if (!normalized) {
        return { valid: false, error: "required" };
      }

      if (prefix && !normalized.startsWith(prefix.toLowerCase())) {
        return { valid: false, error: "prefix" };
      }

      if (!isValidPackageUserFriendlyId(normalized)) {
        return { valid: false, error: "invalid" };
      }

      const duplicate = packageIds.some(
        (item) => item.userFriendlyId.trim().toLowerCase() === normalized && item.id !== currentPackageId,
      );

      if (duplicate) {
        return { valid: false, error: "duplicate" };
      }

      return { valid: true };
    },
    [packageIds, currentPackageId, prefix],
  );

  return {
    prefix,
    validate,
    nextUserFriendlyId,
    isLoading: isDataLoading || isNextIdLoading,
  };
}
